import { OnErrorFn } from '@formatjs/intl'
import { createIntl, createIntlCache } from 'react-intl'
import enUS from '../../../lang/en-US.json'

type Handler = (locale: string) => void
const subscribers = new Set<Handler>()
const cache = createIntlCache()

const getNavigatorLanguage = () => {
  return navigator.language || 'en-US'
}

const onError: OnErrorFn = err => {
  if (err.code === 'MISSING_TRANSLATION') {
    return
  }
  console.error('Intl error:', err)
}

async function getMessages(locale: string) {
  switch (locale) {
    case 'en-XA':
      return await import(/* webpackChunkName: "en-xa" */ '../../../lang/en-XA.json')
    case 'xx-LS':
      return await import(/* webpackChunkName: "xx-ls" */ '../../../lang/xx-LS.json')
    case 'de-DE':
      return await import(/* webpackChunkName: "de-de" */ `../../../lang/de-DE.json`)
    case 'es-ES':
      return await import(/* webpackChunkName: "es-es" */ `../../../lang/es-ES.json`)
    case 'fr-FR':
      return await import(/* webpackChunkName: "fr-fr" */ `../../../lang/fr-FR.json`)
    case 'en-GB':
      return await import(/* webpackChunkName: "en-gb" */ `../../../lang/en-GB.json`)
    default:
      return await import(/* webpackChunkName: "en-us" */ '../../../lang/en-US.json')
  }
}

const AVAILABLE_LOCALES = ['en-us', 'fr-fr', 'de-de', 'es-es', 'en-xa', 'xx-ls', 'en-gb']

function formatLocale(localeString: string): string {
  const locale = localeString.toLowerCase()
  if (!AVAILABLE_LOCALES.includes(locale)) {
    return 'en-US'
  }

  const [lang, region] = locale.split('-')
  return `${lang}-${region.toUpperCase()}`
}

export let intl = createIntl(
  {
    locale: getNavigatorLanguage(),
    defaultLocale: 'en-US',
    onError,
  },
  cache
)

export const refreshLocaleData = async (localeString: string) => {
  const locale = formatLocale(localeString)

  intl = createIntl(
    {
      locale: locale,
      defaultLocale: 'en-US',
      onError,
      messages: typeof document !== 'undefined' ? (await getMessages(locale)).default : enUS,
    },
    cache
  )

  for (const subs of subscribers.values()) {
    subs(locale)
  }

  return intl
}

export const subscribeToLocaleChanges = (handlerFunction: Handler) => {
  subscribers.add(handlerFunction)
  return () => subscribers.delete(handlerFunction)
}

export const getIntl = () => intl
